import type { Messages } from "./zh-CN";

// English copy. Must stay structurally identical to zh-CN.ts (typed via Messages).
const enUS: Messages = {
  common: {
    language: "Language",
    confirm: "Confirm",
    cancel: "Cancel",
    save: "Save",
    delete: "Delete",
    edit: "Edit",
    search: "Search",
    loading: "Loading…",
    empty: "No data",
  },
  nav: {
    workbench: "Workspace",
    platform: "Platform",
    myProjects: "My Projects",
    overview: "Overview",
    summarySettings: "Summary",
    growth: "Growth",
    projectSettings: "Project Settings",
    account: "Account",
    aiCapability: "AI Capabilities",
    dataGrowth: "Data & Growth",
    dataAccount: "Billing & Account",
    accessHelp: "Integration & Help",
    admin: "Admin",
    home: "Home",
    knowledge: "Knowledge Base",
    agents: "Agents",
    aiChat: "Chat & Widget",
    podcast: "AI Podcast",
    mcp: "MCP",
    models: "Model Center",
    analytics: "Site Analytics",
    agentAudit: "Agent Audit",
    chatHistory: "Chat History",
    tokenUsage: "Usage",
    myOrders: "My Orders",
    invitation: "Invitations",
    codeGenerator: "Website Setup",
    api: "Chat API",
    docs: "Docs",
    about: "About",
  },
  admin: {
    userManagement: "Users",
    productManagement: "Products",
    orderManagement: "Orders",
    redeemCode: "Redeem Codes",
    dataControl: "Data Control",
    modelGateway: "Model Gateway",
    systemSettings: "System Settings",
  },
  user: {
    settings: "Settings",
    orders: "My Orders",
    usage: "Usage",
    logout: "Log out",
  },
  mcpServer: {
    needVip: "An annual membership is required to use the MCP client",
    vipDesc: "Upgrade to an annual membership to use the MCP client and connect third-party tool services",
    backHome: "Go home to upgrade",
    serverTab: "MCP Servers",
    clientTab: "MCP Client",
  },
  payment: {
    invalidRedeem: "Please enter a valid redeem code",
    redeemSuccess: "Redeemed successfully",
  },
  modelCenter: {
    type: { chat: "Chat", embedding: "Embedding", rerank: "Rerank", image: "Image" },
    filterAll: "All",
    filterTools: "Tool calls",
    filterImage: "Image",
    empty: "No models match the filter",
    modelN: "Model {{id}}",
    default: "Default",
    basicChat: "Basic chat",
    context: "Context",
    maxOutput: "Max output",
    pendingPrice: "Pricing pending",
    input: "Input",
    output: "Output",
    fetchFailed: "Failed to load models",
    fetchFailedRetry: "Failed to load models, please retry",
  },
  oauth: {
    noCode: "Authorization failed: no authorization code",
    loginSuccess: "Signed in with {{provider}}!",
    bindSuccess: "{{provider}} account linked",
    opFailed: "{{provider}} action failed",
    opFailedRetry: "{{provider}} action failed, please retry",
    processing: "Processing {{provider}} sign-in...",
  },
  auth: {
    email: "Email",
    password: "Password",
    confirmPassword: "Confirm password",
    newPassword: "New password",
    confirmNewPassword: "Confirm new password",
    verifyCode: "Verification code",
    emailVerifyCode: "Email code",
    imageCaptcha: "Captcha",
    passkeyLogin: "Sign in with passkey",
    passwordLogin: "Sign in with password",
    usePassword: "Use password",
    usePasskey: "Use passkey",
    register: "Sign up",
    registerTitle: "Create account",
    resetTitle: "Reset password",
    resetPassword: "Reset password",
    noAccount: "No account yet? Sign up",
    haveAccount: "Already have an account? Sign in",
    forgotPassword: "Forgot password?",
    backToLogin: "Back to sign in",
    sendCode: "Send code",
    getCaptcha: "Get captcha",
    retryAfter: "Retry in {{n}}s",
    otherLogin: "Other sign-in methods",
    githubLogin: "GitHub",
    qqLogin: "QQ",
    legacyTip: "Legacy Summary AI / PostChat users: sign in directly with QQ, no registration needed.",
    autoReturnTip: "You'll be returned to your previous page after signing in.",
    autoLoggingIn: "Signing in, please wait...",
    captchaAlt: "Captcha",
    operatedBy: "Operated by Beijing Honghui Technology Store",
    userAgreement: "Terms of Service",
    privacy: "Privacy Policy",
    rules: {
      requiredEmail: "Please enter your email",
      invalidEmail: "Please enter a valid email address",
      requiredPassword: "Please enter your password",
      requiredVerifyCode: "Please enter the verification code",
      requiredEmailCode: "Please enter the email code",
      requiredCaptcha: "Please enter the captcha",
      requiredConfirm: "Please confirm your password",
      requiredNewPassword: "Please enter a new password",
      requiredConfirmNew: "Please confirm the new password",
      passwordMin8: "Password must be at least 8 characters",
      passwordMin6: "Password must be at least 6 characters",
      passwordPattern: "Password must contain letters and digits",
      passwordMismatch: "The two passwords do not match",
    },
    msg: {
      enterEmailFirst: "Please enter your email first",
      passkeyFailed: "Passkey sign-in failed",
      passkeyVerifyFailed: "Passkey verification failed",
      passkeyTryPassword: "Passkey sign-in failed, please try password sign-in",
      opCancelled: "Operation cancelled or not permitted",
      securityError: "Security error, possibly a cross-origin issue",
      opAborted: "Operation aborted",
      loginSuccessConsole: "Signed in, entering the console",
      loginSuccessBack: "Signed in, returning to your previous page",
      linkInvalid: "Invalid sign-in link, please request a new one",
      linkExpired: "Sign-in link expired, please request a new one",
      loginFailedManual: "Sign-in failed, please try manual sign-in",
      tooManyAttempts: "Too many attempts, please try again later",
      wrongCredentials: "Incorrect username or password",
      enterValidEmailPwd: "Please enter a valid email and password",
      loginFailedRetry: "Sign-in failed, please retry",
      loginFailed: "Sign-in failed",
      systemError: "System error, please try again later",
      registerSuccess: "Registered successfully!",
      registerFailed: "Registration failed",
      codeSent: "Verification code sent",
      sendCodeFailed: "Failed to send code",
      captchaFailed: "Failed to get captcha",
      resetSuccess: "Password reset successfully!",
      resetFailed: "Password reset failed",
      resetFailedRetry: "Password reset failed, please retry",
    },
  },
  siteWorkspace: {
    back: "All sites", aria: "Site features", overview: "Overview", copilot: "Copilot", playbook: "Playbooks", knowledge: "Knowledge", abilities: "Abilities", test: "Test", appearance: "Appearance", analytics: "Analytics", settings: "Settings", publish: "Publish",
    common: { retry: "Retry", draft: "Draft", published: "Published", healthy: "Healthy", degraded: "Degraded", confirmRequired: "Confirm before execution", noData: "No data" },
    risk: { read: "Read only", draft: "Draft", write: "Write", sensitive: "Sensitive", financial: "Financial", irreversible: "Irreversible" },
    release: { ownerCanary: "Owner only", percent1: "1% of visitors", percent10: "10% of visitors", percent50: "50% of visitors", all: "All visitors" },
  },
};

export default enUS;
